import React, { useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Edit, ArrowLeft, Save, Eye, Download, Video, Plus, X, Upload } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { Resource } from '../lib/types';
import { getCurrentUser } from '../lib/auth';
import TextEditor from './TextEditor';

const ResourcePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = getCurrentUser();
  const isAdmin = user?.role === 'admin';
  const [isEditing, setIsEditing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Mock data - In a real app, fetch from API
  const [resource, setResource] = useState<Resource>({
    id: '1',
    title: 'Guide complet pour rédiger des offres d\'emploi efficaces',
    description: 'Découvrez les meilleures pratiques pour créer des offres d\'emploi qui attirent les meilleurs talents.',
    type: 'PDF',
    rating: 4.8,
    downloads: 1245,
    imageUrl: 'https://images.unsplash.com/photo-1586281380349-632531db7ed4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
    videoUrl: '',
    introduction: 'Dans ce guide complet, nous allons explorer les techniques essentielles pour rédiger des offres d\'emploi qui attirent les meilleurs talents.',
    content: `<h2>Les fondamentaux d'une offre d'emploi efficace</h2>
<p>Une offre d'emploi efficace doit être claire, concise et attractive. Voici les éléments essentiels à inclure :</p>
<ul>
  <li>Un titre de poste clair et précis</li>
  <li>Une description détaillée des responsabilités</li>
  <li>Les compétences requises et souhaitées</li>
  <li>Les avantages et bénéfices offerts</li>
</ul>

<h2>Structure recommandée</h2>
<p>Pour maximiser l'impact de votre offre, suivez cette structure :</p>
<ol>
  <li>Introduction attractive</li>
  <li>Présentation de l'entreprise</li>
  <li>Description du poste</li>
  <li>Profil recherché</li>
  <li>Ce que nous offrons</li>
  <li>Comment postuler</li>
</ol>

<h2>Conseils pour se démarquer</h2>
<p>Pour rendre votre offre plus attractive :</p>
<ul>
  <li>Utilisez un ton authentique qui reflète votre culture d'entreprise</li>
  <li>Mettez en avant vos valeurs et votre mission</li>
  <li>Soyez transparent sur le processus de recrutement</li>
  <li>Incluez des témoignages d'employés</li>
</ul>`,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  });

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      // Create a temporary URL for the dropped file
      const imageUrl = URL.createObjectURL(file);
      
      // Update the resource state with the new image URL
      setResource(prevResource => ({
        ...prevResource,
        imageUrl: imageUrl
      }));

      // In a real application, you would upload the file to a server here
      // and update the imageUrl with the returned URL from the server
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif']
    },
    multiple: false
  });

  const handleSave = () => {
    // In a real app, save to API
    setIsEditing(false);
  };

  const getYouTubeEmbedUrl = (url: string) => {
    if (!url) return '';
    
    try {
      const videoId = url.match(/(?:youtu\.be\/|youtube\.com(?:\/embed\/|\/v\/|\/watch\?v=|\/watch\?.+&v=))([\w-]{11})/);
      return videoId ? `https://www.youtube.com/embed/${videoId[1]}` : '';
    } catch {
      return '';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        {/* Back button */}
        <div className="mb-8">
          <Link
            to="/learn"
            className="inline-flex items-center text-gray-600 hover:text-gray-800 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            <span>Retour aux ressources</span>
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Image de couverture avec drag & drop */}
          <div className="h-64 relative">
            {isEditing ? (
              <div
                {...getRootProps()}
                className={`h-full flex flex-col items-center justify-center border-2 border-dashed transition-colors cursor-pointer relative
                  ${isDragActive ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300 hover:border-indigo-500 hover:bg-gray-50'}`}
              >
                <input {...getInputProps()} />
                <Upload className={`w-12 h-12 mb-4 ${isDragActive ? 'text-indigo-500' : 'text-gray-400'}`} />
                <p className="text-gray-600 text-center px-4">
                  {isDragActive ? (
                    "Déposez l'image ici..."
                  ) : (
                    <>
                      Glissez et déposez une image ici, ou<br />
                      <span className="text-indigo-600">cliquez pour sélectionner un fichier</span>
                    </>
                  )}
                </p>
                {resource.imageUrl && (
                  <div className="absolute inset-0">
                    <img
                      src={resource.imageUrl}
                      alt={resource.title}
                      className="w-full h-full object-cover opacity-20"
                    />
                  </div>
                )}
              </div>
            ) : (
              <img
                src={resource.imageUrl}
                alt={resource.title}
                className="w-full h-full object-cover"
              />
            )}
          </div>

          <div className="p-8">
            {/* Titre */}
            {isEditing ? (
              <input
                type="text"
                value={resource.title}
                onChange={(e) => setResource({ ...resource, title: e.target.value })}
                className="w-full text-3xl font-bold mb-4 p-2 border border-gray-300 rounded"
              />
            ) : (
              <h1 className="text-3xl font-bold mb-4">{resource.title}</h1>
            )}

            {/* Vidéo d'introduction */}
            <div className="mb-8">
              {isEditing ? (
                <div className="space-y-4">
                  <div className="flex items-center">
                    <Video className="w-5 h-5 text-gray-500 mr-2" />
                    <h2 className="text-lg font-semibold">Vidéo d'introduction</h2>
                  </div>
                  
                  {resource.videoUrl ? (
                    <div className="relative">
                      <input
                        type="text"
                        value={resource.videoUrl}
                        onChange={(e) => setResource({ ...resource, videoUrl: e.target.value })}
                        className="w-full p-2 border border-gray-300 rounded pr-10"
                        placeholder="URL de la vidéo YouTube (ex: https://www.youtube.com/watch?v=...)"
                      />
                      <button
                        onClick={() => setResource({ ...resource, videoUrl: '' })}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-red-500"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setResource({ ...resource, videoUrl: '' })}
                      className="flex items-center text-indigo-600 hover:text-indigo-800"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      <span>Ajouter une vidéo YouTube</span>
                    </button>
                  )}
                  
                  {resource.videoUrl && getYouTubeEmbedUrl(resource.videoUrl) && (
                    <div className="relative pb-[56.25%] h-0 bg-gray-100 rounded-lg">
                      <iframe
                        src={getYouTubeEmbedUrl(resource.videoUrl)}
                        className="absolute top-0 left-0 w-full h-full rounded-lg"
                        allowFullScreen
                      />
                    </div>
                  )}
                </div>
              ) : resource.videoUrl && getYouTubeEmbedUrl(resource.videoUrl) ? (
                <div className="relative pb-[56.25%] h-0">
                  <iframe
                    src={getYouTubeEmbedUrl(resource.videoUrl)}
                    className="absolute top-0 left-0 w-full h-full rounded-lg"
                    allowFullScreen
                  />
                </div>
              ) : null}
            </div>

            {/* Introduction */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4">Introduction</h2>
              {isEditing ? (
                <textarea
                  value={resource.introduction}
                  onChange={(e) => setResource({ ...resource, introduction: e.target.value })}
                  className="w-full p-4 border border-gray-300 rounded"
                  rows={4}
                />
              ) : (
                <p className="text-gray-700 leading-relaxed">{resource.introduction}</p>
              )}
            </div>

            {/* Contenu principal */}
            <div className="prose max-w-none">
              {isEditing ? (
                <TextEditor
                  value={resource.content}
                  onChange={(content) => setResource({ ...resource, content })}
                  height="500px"
                />
              ) : (
                <div dangerouslySetInnerHTML={{ __html: resource.content }} />
              )}
            </div>

            {/* Actions */}
            <div className="mt-8 flex justify-end space-x-4">
              <button className="flex items-center space-x-2 text-gray-600 hover:text-gray-800">
                <Eye className="w-4 h-4" />
                <span>{resource.downloads} vues</span>
              </button>
              <button className="flex items-center space-x-2 text-indigo-600 hover:text-indigo-800">
                <Download className="w-4 h-4" />
                <span>Télécharger</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResourcePage;